import { type ReactNode, useEffect, useRef } from 'react';
import { ClassroomLinkCard, CreateClassroomButton, CreateClassroomImageButton } from '@/shared/features/classroom/components';
import { useClassroomLinking } from '@/shared/features/classroom/hooks';
import { type ContentShareType } from '@/shared/features/classroom/types';
import { trackClassroomEvent } from '@/shared/features/classroom/utils';
import { useCanCreateClassroom } from '@/shared/hooks';
import { TooltipProvider } from '@/ui_v3';
import { cn } from '@/utils/tailwind.util';
export type { ContentShareType };
export function LinkContentToClassroomSection({
  contentId,
  contentType
}: {
  contentId: string;
  contentType: ContentShareType;
}) {
  const {
    classrooms,
    isLoadingClassrooms,
    isInitialized,
    canLoadClassrooms,
    isPending,
    linkClassroom,
    unlinkClassroom
  } = useClassroomLinking({
    contentId,
    contentType,
    source: 'content_share_modal'
  });
  const hasTrackedViewRef = useRef(false);
  const canCreateClassroom = useCanCreateClassroom();
  let classroomListContent: ReactNode;
  const hasClassrooms = canLoadClassrooms && classrooms.length > 0;
  useEffect(() => {
    if (!isInitialized || isLoadingClassrooms || hasTrackedViewRef.current) {
      return;
    }
    trackClassroomEvent('view_link_to_classroom_section', {
      source: 'content_share_modal',
      contentId,
      contentType,
      hasClassrooms,
      classroomCount: classrooms.length
    });
    hasTrackedViewRef.current = true;
  }, [classrooms.length, contentId, contentType, hasClassrooms, isInitialized, isLoadingClassrooms]);
  if (!isInitialized || isLoadingClassrooms) {
    return null;
  }
  if (!hasClassrooms) {
    classroomListContent = null;
  } else {
    classroomListContent = classrooms.map(classroom => <ClassroomLinkCard key={classroom.classGroupId} className={classrooms.length === 1 ? 'mobile-lg:col-span-2 mobile-lg:mx-auto' : undefined} classroom={classroom} isPending={isPending} isLinked={Boolean(classroom.isLinked)} linkedClassLinkId={classroom.linkedClassLinkId ?? undefined} onLink={linkClassroom} onUnlink={unlinkClassroom} />);
  }
  return <TooltipProvider delayDuration={0} data-sentry-element="TooltipProvider" data-sentry-component="LinkContentToClassroomSection" data-sentry-source-file="LinkContentToClassroomSection.tsx">
      <aside className={cn('relative z-50 w-full shrink-0 overflow-x-auto overflow-y-visible mobile-lg:flex mobile-lg:h-full mobile-lg:min-h-0 mobile-lg:w-[198px] mobile-lg:overflow-visible mobile-lg:border-t-0', hasClassrooms && 'border-t border-alternative')}>
        <div className={cn('relative z-50 flex max-w-none gap-0 overflow-visible pb-md pt-xs mobile-lg:min-h-0 mobile-lg:w-[198px] mobile-lg:flex-1 mobile-lg:flex-col mobile-lg:gap-sm mobile-lg:px-0 mobile-lg:pb-0 mobile-lg:pt-0', hasClassrooms ? 'w-max px-xs' : 'w-full px-0')}>
          {canCreateClassroom && (hasClassrooms ? <CreateClassroomButton className="max-mobile-lg:h-[115px] max-mobile-lg:w-[90px] max-mobile-lg:min-w-[90px] max-mobile-lg:shrink-0 max-mobile-lg:border-b-0" onBeforeOpenClassroom={() => trackClassroomEvent('click_create_classroom', {
          source: 'content_share_modal',
          contentId,
          contentType,
          hasClassrooms
        })} /> : <CreateClassroomImageButton onBeforeOpenClassroom={() => trackClassroomEvent('click_create_classroom', {
          source: 'content_share_modal',
          contentId,
          contentType,
          hasClassrooms
        })} />)}
          {hasClassrooms && <div className="relative z-50 flex gap-0 mobile-lg:grid mobile-lg:min-h-0 mobile-lg:flex-1 mobile-lg:grid-cols-2 mobile-lg:content-start mobile-lg:gap-3xs mobile-lg:overflow-y-auto">
              {classroomListContent}
            </div>}
        </div>
      </aside>
    </TooltipProvider>;
}